import api, { Schemas } from '~/clients/api-client'
import optimus_api from '~/clients/optimus-client'

import { withCreate } from './middleware/withCreate'
import { withImport } from './middleware/withImport'
import { withOptimisticDeletion } from './middleware/withOptimisticDeletion'
import { withOptimisticUpdate } from './middleware/withOptimisticUpdate'
import { CommentsMessage, CommentsThread, InboxEntry, RecurringAssignment } from './selectors'

export * from './di-entity.api.generated'

// Inconsistent naming overrides
export const importPractitionerScheduleLocations = withImport(api.fetchPractitionerScheduleLocations)
export const importPractitionerScheduleStatuses = withImport(api.fetchPractitionerScheduleStatuses)

// The special filter endpoint, currently only used for SurgeryFolderMetadata
export const importFilterSurgeryFolderMetadata = withImport(api.filterSurgeryFolderMetadata)
export const importLatestSurgeryMetadataSuggestions = withImport(api.fetchLatestSurgeryMetadataSuggestions)
export const importPlanningNoteMigrationRecords = withImport(api.fetchPlanningNoteMigrations)

// OPTIMUS
export const importSurgeryProgramJobs = withImport(optimus_api.fetchSurgeryProgramJobs)

// Recurring assignments
export const importRecurringAssignments = withImport(api.fetchRecurringAssignments)
export const archiveRecurringAssignment = (id: number | string) =>
    withOptimisticUpdate((id, _body: Partial<RecurringAssignment>) => api.archiveRecurringAssignment(id), 'recurringAssignments')(id, {
        entity_status: 'archived',
    })
export const rejectRecurringAssignment = (id: number | string) =>
    withOptimisticUpdate((id, _body: Partial<RecurringAssignment>) => api.rejectRecurringAssignment(id), 'recurringAssignments')(id, {
        entity_status: 'rejected',
    })
export const activateRecurringAssignment = (id: number | string) =>
    withOptimisticUpdate((id, _body: Partial<RecurringAssignment>) => api.activateRecurringAssignment(id), 'recurringAssignments')(id, {
        entity_status: 'active',
    })
type UpdateRecurringAssignmentBody = Schemas['RecurringAssignmentCreate']
export const updateRecurringAssignment = (id: number | string, body: UpdateRecurringAssignmentBody) =>
    withOptimisticUpdate((id, body: UpdateRecurringAssignmentBody) => api.updateRecurringAssignment(id, body), 'recurringAssignments')(id, body)

// CommentsThreads
export const createCommentsThread = withCreate(api.createCommentsThread)
export const editCommentsThread = (threadId: number, { raw_content, mentions, updated_by }: Schemas['EditCommentsThreadRequest'] & { updated_by: string }) =>
    withOptimisticUpdate((id: number, _body: Partial<CommentsThread>) => api.editCommentsThread(id, { raw_content, mentions }), 'commentsThreads')(threadId, {
        content: {
            raw_content,
            updated_at: new Date().toISOString(),
            updated_by,
        } as CommentsThread['content'],
        updated_at: new Date().toISOString(),
    } satisfies Partial<CommentsThread>)

export const createCommentsThreadMessage = withCreate(api.createCommentsThreadMessage)
export const editCommentsThreadMessage = (
    messageId: number,
    { raw_content, mentions, updated_by }: Schemas['EditCommentsMessageRequest'] & { updated_by: string }
) =>
    withOptimisticUpdate((id: number, _body: Partial<CommentsMessage>) => api.editCommentsThreadMessage(id, { raw_content, mentions }), 'commentsMessages')(
        messageId,
        {
            content: {
                raw_content,
                updated_at: new Date().toISOString(),
                updated_by,
            } as CommentsMessage['content'],
            updated_at: new Date().toISOString(),
        } satisfies Partial<CommentsMessage>
    )
export const createCommentsThreadReaction = withCreate(api.createCommentsThreadReaction)
export const createCommentsThreadMessageReaction = withCreate(api.createCommentsThreadMessageReaction)
export const deleteCommentsThreadReaction = (threadId: number, reactionId: number) =>
    withOptimisticDeletion((id: number) => api.deleteCommentsThreadReaction({ threadId, reactionId: id }), 'commentsThreadReactions')(reactionId)
export const deleteCommentsThreadMessageReaction = (messageId: number, reactionId: number) =>
    withOptimisticDeletion(
        (id: number) => api.deleteCommentsThreadMessageReaction({ messageId: messageId, reactionId: id }),
        'commentsMessageReactions'
    )(reactionId)
export const resolveCommentsThread = (threadId: number, resolvedBy: string) =>
    withOptimisticUpdate((id: number, _body: Partial<CommentsThread>) => api.resolveCommentsThread(id), 'commentsThreads')(threadId, {
        resolved: true,
        resolved_at: new Date().toISOString(),
        resolved_by: resolvedBy,
    })
export const unresolveCommentsThread = (threadId: number) =>
    withOptimisticUpdate((id: number, _body: Partial<CommentsThread>) => api.unresolveCommentsThread(id), 'commentsThreads')(threadId, {
        resolved: false,
        resolved_at: null,
        resolved_by: null,
    })
export const archiveCommentsThread = (threadId: number, archivedBy: string) =>
    withOptimisticUpdate((id: number, _body: Partial<CommentsThread>) => api.archiveCommentsThread(id), 'commentsThreads')(threadId, {
        archived: true,
        archived_at: new Date().toISOString(),
        archived_by: archivedBy,
    })
export const unarchiveCommentsThread = (threadId: number) =>
    withOptimisticUpdate((id: number, _body: Partial<CommentsThread>) => api.unarchiveCommentsThread(id), 'commentsThreads')(threadId, {
        archived: false,
        archived_at: null,
        archived_by: null,
    })
export const pinCommentsThread = (threadId: number, pinnedBy: string) =>
    withOptimisticUpdate((id: number, _body: Partial<CommentsThread>) => api.pinCommentsThread(id), 'commentsThreads')(threadId, {
        pinned: true,
        pinned_at: new Date().toISOString(),
        pinned_by: pinnedBy,
    })
export const unpinCommentsThread = (threadId: number) =>
    withOptimisticUpdate((id: number, _body: Partial<CommentsThread>) => api.unpinCommentsThread(id), 'commentsThreads')(threadId, {
        pinned: false,
        pinned_at: null,
        pinned_by: null,
    })

// inbox threads

export const markInboxEntryAsRead = (inboxEntryId: number) =>
    withOptimisticUpdate((id: number, _body: Partial<InboxEntry>) => api.markInboxEntryAsRead(id), 'inboxEntries')(inboxEntryId, {
        is_read: true,
        read_at: new Date().toISOString(),
    } satisfies Partial<InboxEntry>)
